import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  ArrowLeft, 
  FileText, 
  Layout, 
  Link as LinkIcon,
  Search,
  Filter,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const backendUrl = 'https://justurbane.in/backend_php/public';
const frontendUrl = 'https://justurbane.in'; 

const AdmincarouselEditorPage = () => {
  const navigate = useNavigate();
  const { carouselId } = useParams();

  const [carousel, setCarousel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [activeTab, setActiveTab] = useState('manual');
  const [allArticles, setAllArticles] = useState([]);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const initData = async () => {
      try {
        await Promise.all([
          fetchCarousel(token),
          fetchDynamicCategories(token),
          fetchAllArticles(token)
        ]);
      } catch (err) {
        console.error("Initialization error", err);
      } finally {
        setLoading(false);
      }
    };

    initData();
  }, [navigate, carouselId]);

  const fetchDynamicCategories = async (token) => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/categories/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (Array.isArray(response.data)) {
        setDynamicCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to load categories', error);
    }
  };

  const fetchCarousel = async (token) => {
    if (!carouselId || carouselId === 'new') {
      setCarousel({
        id: null, 
        title: '', 
        description: '', 
        category: '', 
        image_url: '', 
        link: '', 
      });
      return;
    }

    try {
      const response = await fetch(
        `${backendUrl}/api/admin/carousel/${carouselId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCarousel(data);
      }
    } catch (error) {
      toast.error('Failed to load carousel');
      navigate('/admin/carousel');
    }
  };

  const fetchAllArticles = async (token) => {
    try {
      setLoadingArticles(true);
      const response = await fetch(`${backendUrl}/api/admin/articles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const articles = Array.isArray(data) ? data : (data.data || data.articles || []);
        setAllArticles(articles);
      }
    } catch (error) {
      toast.error('Could not load article list');
    } finally {
      setLoadingArticles(false);
    }
  };

  const filteredArticles = useMemo(() => {
    return allArticles.filter(article => {
      const matchesCategory = selectedCategoryFilter === 'all' || 
        String(article.category) === String(selectedCategoryFilter);

      const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (article.summary && article.summary.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesCategory && matchesSearch;
    });
  }, [allArticles, searchQuery, selectedCategoryFilter]);

  const handleArticleSelect = (articleId) => {
    if (!articleId) return;

    const article = allArticles.find(a => String(a.id) === String(articleId));

    if (article) {
      const articleLink = `${frontendUrl}/article/${article.slug}`;
      
      let rawImagePath = article.hero_image || article.image || '';
      let previewImage = rawImagePath;
      
      if (previewImage && !previewImage.startsWith('http')) {
        previewImage = `${backendUrl}/storage/${previewImage}`;
      }

      setCarousel(prev => ({
        ...prev,
        title: article.title || '',
        description: article.summary || article.short_description || article.description || '', 
        category: article.category || '', 
        image_url: previewImage,
        link: articleLink
      }));

      toast.success('Details populated from article!');
      setActiveTab('manual');
    }
  };

  const handleSave = async () => {
    if (!carousel.title || !carousel.description || !carousel.category) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();

      formData.append('title', carousel.title);
      formData.append('description', carousel.description);
      formData.append('category', carousel.category);
      formData.append('link', carousel.link || ''); 
      
      if (carousel.image_url instanceof File) {
        formData.append('image_url', carousel.image_url);
      } 
      else if (typeof carousel.image_url === 'string' && carousel.image_url.startsWith('http')) {
        formData.append('image_url_string', carousel.image_url);
      }
   
      const url = carousel.id 
        ? `${backendUrl}/api/admin/carousel/${carousel.id}`
        : `${backendUrl}/api/admin/carousel`;
   
      if (carousel.id) {
        formData.append('_method', 'PUT');
      }

      const response = await fetch(url, {
        method: 'POST', 
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
   
      if (response.ok) {
        toast.success(carousel.id ? 'Carousel updated!' : 'Carousel created!');
        navigate(`/admin/carousel`);
      } else {
        const error = await response.json();
        throw new Error(error.message || error.detail || 'Save failed');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };
      
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <header className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/carousel')}
                className="group p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-gray-600 group-hover:text-gray-900" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {carousel?.id ? 'Edit carousel' : 'New carousel'}
                </h1>
                <p className="text-sm text-gray-500">Carousel Management</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-amber-200 transition-all active:scale-95 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                <span>{saving ? 'Saving...' : 'Publish carousel'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-10 flex justify-center">
          <div className="bg-white p-1.5 rounded-2xl shadow-sm border border-gray-200 inline-flex">
            <button
              onClick={() => setActiveTab('manual')}
              className={`flex items-center space-x-3 px-8 py-3 rounded-xl transition-all ${
                activeTab === 'manual' 
                ? 'bg-amber-500 text-white shadow-lg' 
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <Layout className="w-5 h-5" />
              <span className="font-bold uppercase tracking-wider text-sm">Manual Design</span>
            </button>
            <button
              onClick={() => setActiveTab('article')}
              className={`flex items-center space-x-3 px-8 py-3 rounded-xl transition-all ${
                activeTab === 'article' 
                ? 'bg-amber-500 text-white shadow-lg' 
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-5 h-5" />
              <span className="font-bold uppercase tracking-wider text-sm">Import Article</span>
            </button>
          </div>
        </div>

        <div className="space-y-8">
          {activeTab === 'article' && (
            <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="text-center mb-8">
                <div className="inline-flex p-3 rounded-2xl bg-amber-50 mb-4">
                  <Search className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Select Source Content</h2>
                <p className="text-gray-500 mt-2">Find and select an article to automatically populate your carousel slide.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by title..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-11 pr-10 py-4 bg-gray-50 border-transparent focus:bg-white focus:ring-2 focus:ring-amber-500 focus:border-transparent rounded-2xl text-gray-900 transition-all"
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Filter className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    value={selectedCategoryFilter}
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                    className="block w-full pl-11 pr-4 py-4 bg-gray-50 border-transparent focus:bg-white focus:ring-2 focus:ring-amber-500 focus:border-transparent rounded-2xl text-gray-900 appearance-none transition-all cursor-pointer"
                  >
                    <option value="all">All Categories</option>
                    {dynamicCategories.map(cat => (
                      <option key={cat.id} value={cat.slug || cat.name}>{cat.display_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="border border-gray-100 rounded-2xl overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                  {loadingArticles ? (
                    <div className="p-12 text-center">
                      <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto mb-4" />
                      <p className="text-gray-500">Fetching latest articles...</p>
                    </div>
                  ) : filteredArticles.length > 0 ? (
                    <div className="divide-y divide-gray-50">
                      {filteredArticles.map((article) => (
                        <div 
                          key={article.id}
                          onClick={() => handleArticleSelect(article.id)}
                          className="group p-5 hover:bg-amber-50 cursor-pointer transition-all flex items-start space-x-4"
                        >
                          <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gray-100 overflow-hidden border border-gray-100">
                             <img 
                                src={article.hero_image?.startsWith('http') ? article.hero_image : `${backendUrl}/storage/${article.hero_image || article.image}`} 
                                alt="" 
                                className="w-full h-full object-cover"
                                onError={(e) => e.target.src = 'https://via.placeholder.com/150'}
                             />
                          </div>
                          <div className="flex-grow">
                            <h4 className="font-bold text-gray-900 group-hover:text-amber-700 transition-colors line-clamp-1">{article.title}</h4>
                            <div className="flex items-center mt-1 space-x-3">
                              <span className="text-xs font-bold text-amber-600 uppercase tracking-tighter bg-amber-100 px-2 py-0.5 rounded">
                                {article.category || 'Uncategorized'}
                              </span>
                              <span className="text-xs text-gray-400">ID: #{article.id}</span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="bg-amber-500 text-white p-2 rounded-full">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-20 text-center bg-gray-50/50">
                      <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 font-medium">No articles match your criteria</p>
                      <button 
                        onClick={() => {setSearchQuery(''); setSelectedCategoryFilter('all');}}
                        className="text-amber-600 text-sm mt-2 hover:underline"
                      >
                        Clear filters and search
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'manual' && (
            <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 uppercase tracking-wider ml-1">carousel Title *</label>
                    <input
                      type="text"
                      value={carousel?.title || ''}
                      onChange={(e) => setCarousel({ ...carousel, title: e.target.value })}
                      className="w-full px-5 py-4 text-lg font-medium bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:ring-2 focus:ring-amber-500 transition-all outline-none"
                      placeholder="Enter a compelling title..."
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 uppercase tracking-wider ml-1">Target Category *</label>
                    <div className="relative">
                      <select
                        value={carousel?.category || ''}
                        onChange={(e) => setCarousel({ ...carousel, category: e.target.value })}
                        className="w-full px-5 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:ring-2 focus:ring-amber-500 transition-all outline-none appearance-none cursor-pointer"
                      >
                        <option value="">Choose a category</option>
                        {dynamicCategories.map((cat) => (
                          <option key={cat.id} value={cat.slug || cat.name}>
                            {cat.display_name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                        <Filter className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-wider ml-1">Description / Hook *</label>
                  <textarea
                    value={carousel?.description || ''}
                    onChange={(e) => setCarousel({ ...carousel, description: e.target.value })}
                    rows={4}
                    className="w-full px-5 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:ring-2 focus:ring-amber-500 transition-all outline-none resize-none"
                    placeholder="Write a brief summary to entice readers..."
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-wider ml-1">Destination URL</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-colors group-focus-within:text-amber-500">
                      <LinkIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={carousel?.link || ''}
                      onChange={(e) => setCarousel({ ...carousel, link: e.target.value })}
                      className="w-full pl-14 pr-5 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:ring-2 focus:ring-amber-500 transition-all outline-none"
                      placeholder="https://justurbane.in/article/my-story"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-wider ml-1">Cover Imagery</label>
                  <div 
                    className={`relative border-2 border-dashed rounded-3xl p-10 transition-all text-center
                      ${carousel?.image_url ? 'border-amber-200 bg-amber-50/20' : 'border-gray-200 bg-gray-50 hover:border-amber-400'}`}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) setCarousel({ ...carousel, image_url: file });
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    
                    {!carousel?.image_url ? (
                      <div className="flex flex-col items-center">
                        <div className="p-4 bg-white rounded-2xl shadow-sm mb-4">
                          <Layout className="w-8 h-8 text-amber-500" />
                        </div>
                        <p className="font-bold text-gray-700">Click to upload or drag and drop</p>
                        <p className="text-sm text-gray-400 mt-1">Recommended: 1920x1080 (JPG, PNG)</p>
                      </div>
                    ) : (
                      <div className="relative max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                        <img
                          src={
                            carousel.image_url instanceof File
                              ? URL.createObjectURL(carousel.image_url)
                              : carousel.image_url 
                          }
                          alt="Preview"
                          className="w-full aspect-video object-cover"
                        />
                        <div className="absolute top-4 right-4 flex space-x-2">
                           <button 
                            onClick={(e) => { e.preventDefault(); setCarousel({...carousel, image_url: ''}); }}
                            className="bg-red-500 text-white p-2 rounded-xl shadow-lg hover:bg-red-600 transition-colors"
                           >
                             <X className="w-5 h-5" />
                           </button>
                        </div>
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-6 text-left">
                           <p className="text-white font-bold text-sm">Previewing carousel Image</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmincarouselEditorPage;